import { useContext, useEffect, useMemo, useRef } from "react"
import { useSelector } from "react-redux"
import appConst from "../Utilities/appConst.json"
import {
  getMaterialFromColor,
  getTilePreviewSizes,
  normalizeToNumber,
} from "../Utilities/tilePreview"
import { buildBasketItemImageUrl } from "../Utilities/basketImage"
import { LanguageContext } from "../context/language/LanguageContext"
import SelectedIcon from "../img/selected-icon.svg?react"
import hardCoverImage from "../img/hard.jpg"
import softCoverImage from "../img/soft.jpg"
import layflatCoverImage from "../img/layflat.jpg"
import { CoverPreview } from "../shared/ui/cover-preview"
import { buildCoverPreviewData } from "../shared/ui/cover-preview/utils"
import { BasketItem, DropDownConfig, DropDownOption, DropDownOptionValue } from "../types/basket"

type LanguageContextType = {
  language: { id: string; text: string }
  dictionary: Record<string, string>
  getTranslation: (text_id: string) => string
  setLanguage: (language: { id: string; text: string }) => void
}

interface TuneOptionsPanelProps {
  dropDowns?: DropDownConfig[]
  imageUrl?: string
  item?: BasketItem
}

interface Frame {
  frame_color: string | number
  url?: string
}

type Basket = {
  order_id?: string
}

type RootState = {
  basket?: {
    frames?: Frame[]
    basket?: Basket
  }
}

const getOptionsSignature = (dropDown: DropDownConfig) => {
  const options = Array.isArray(dropDown.array) ? dropDown.array : []

  const signature = {
    isDisabled: dropDown.isDisabled,
    options: options.map((option) => ({
      value: option.value,
      disabled: option.disabled,
    })),
  }

  return JSON.stringify(signature)
}

const toTestIdFragment = (value: DropDownOptionValue | string | undefined) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "unknown"

export default function TuneOptionsPanel({
  dropDowns,
  imageUrl,
  item,
}: TuneOptionsPanelProps) {
  const languageContext = useContext(LanguageContext) as LanguageContextType
  const frames = useSelector<RootState, Frame[]>(
    (state) => state?.basket?.frames ?? []
  )
  const basket = useSelector<RootState, Basket | undefined>(
    (state) => state?.basket?.basket
  )
  const product =
    typeof window !== "undefined"
      ? sessionStorage.getItem(appConst.PRODUCT)
      : undefined
  const isTileProduct =
    product === appConst.TILES ||
    item?.product === appConst.TILES
  const isBookProduct =
    product === appConst.BOOKS ||
    item?.product === appConst.BOOKS
  const baseTileImage = useMemo(() => {
    if (isTileProduct && item) {
      return buildBasketItemImageUrl(item, basket || {})
    }
    return item?.url || imageUrl || ""
  }, [isTileProduct, item, basket, imageUrl])
  const optionSignatureRef = useRef<Record<string, string>>({})
  const coverImageMap: Record<string, string> = {
    [appConst.HARD_CODE]: hardCoverImage,
    [appConst.SOFT_CODE]: softCoverImage,
    [appConst.LAYFLAT_CODE]: layflatCoverImage,
  }
  const coverPreviewData = useMemo(
    () => (isBookProduct ? buildCoverPreviewData(item, baseTileImage) : null),
    [isBookProduct, item, baseTileImage]
  )
  const isWideCoverPreview = (coverPreviewData?.aspectRatio ?? 1) >= 1

  useEffect(() => {
    if (!dropDowns || dropDowns.length === 0) return

    dropDowns.forEach((dropDown) => {
      const currentSignature = getOptionsSignature(dropDown)
      const previousSignature = optionSignatureRef.current[dropDown.id]

      if (previousSignature === currentSignature) {
        return
      }

      optionSignatureRef.current[dropDown.id] = currentSignature

      if (
        dropDown.isDisabled ||
        !dropDown.setValue ||
        !Array.isArray(dropDown.array)
      ) {
        return
      }

      const enabledOptions = dropDown.array.filter((option) => !option.disabled)
      if (enabledOptions.length === 0) return

      const matchingOption = dropDown.array.find(
        (option) => option.value === dropDown.value
      )
      const hasValidSelection = matchingOption && !matchingOption.disabled

      if (!hasValidSelection) {
        dropDown.setValue(enabledOptions[0].value)
      }
    })
  }, [dropDowns])

  if (!dropDowns || dropDowns.length === 0) {
    return null
  }

  const handleOptionClick = (dropDown: DropDownConfig, option: DropDownOption) => {
    if (!dropDown.isDisabled && !option.disabled && dropDown.setValue) {
      dropDown.setValue(option.value)
    }
  }

  const shouldShowImage = (title?: string) => {
    return title === languageContext.getTranslation("format")
  }

  const getFrameOverlay = (color?: DropDownOptionValue) => {
    const normalizedColor = normalizeToNumber(color)
    if (normalizedColor === undefined) return undefined

    const frame =
      frames?.find(
        (frame: Frame) =>
          normalizeToNumber(frame.frame_color) === normalizedColor
      ) || undefined

    return frame?.url as string | undefined
  }

  const getCoverPreviewImage = (value?: DropDownOptionValue) => {
    if (value === undefined || value === null) return undefined

    return coverImageMap[String(value)]
  }

  const renderTilePreview = (
    formatValue?: DropDownOptionValue,
    frameOverlayUrl?: string,
    colorForMaterial?: DropDownOptionValue
  ) => {
    const material = getMaterialFromColor(colorForMaterial)
    const { containerSize, imageSize, frameSize } = getTilePreviewSizes(
      formatValue,
      material
    )
    const hasBaseImage = Boolean(baseTileImage)

    return (
      <div
        className="relative flex items-center justify-center w-full h-full overflow-hidden"
      >
        <div
          className="relative flex items-center justify-center"
          style={{ width: containerSize, height: containerSize, borderRadius: 0 }}
        >
          {hasBaseImage ? (
            <img
              src={baseTileImage}
              alt=""
              className="absolute object-cover shadow-[3px_3px_9px_#DADADA]"
              style={{
                width: imageSize,
                height: imageSize,
                borderRadius: 0,
              }}
            />
          ) : (
            <div
              className="absolute"
              style={{
                width: imageSize,
                height: imageSize,
                backgroundColor: "#ffffff",
                borderRadius: 0,
              }}
            />
          )}

          {frameOverlayUrl && (
            <img
              src={frameOverlayUrl}
              alt=""
              className="absolute object-contain"
              style={{ width: frameSize, height: frameSize, borderRadius: 0 }}
            />
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col font-ploni text-secondary-foreground">
      {dropDowns.map((dropDown, index) => (
        <div key={dropDown.id}>
          <div className="flex flex-col gap-6 py-6">
            <h3 className="text-lg font-semibold text-center">
              {dropDown.title}
            </h3>

            <div
              className="flex flex-wrap gap-6 justify-center"
              data-testid={`tune-options-group-${toTestIdFragment(dropDown.id)}`}
            >
              {dropDown.array &&
                dropDown.array.map((option) => {
                  const isSelected = dropDown.value === option.value
                  const isDisabled = dropDown.isDisabled || option.disabled
                  const optionTestId = `tune-option-${toTestIdFragment(dropDown.id)}-${toTestIdFragment(option.value)}`
                  const showImage = shouldShowImage(dropDown.title)
                  const isCoverDropdown =
                    dropDown.id === "book-cover" ||
                    dropDown.title === languageContext.getTranslation(appConst.COVER)
                  const isTileTypeOption =
                    isTileProduct && dropDown.variant === "tileType"
                  const isTileSizeOption =
                    isTileProduct && dropDown.variant === "tileSize"
                  const frameOverlayForTile =
                    isTileTypeOption || isTileSizeOption
                      ? option.frameUrl ||
                        getFrameOverlay(
                          isTileTypeOption
                            ? option.value
                            : item?.frame_color
                        ) ||
                        (isTileSizeOption ? item?.itemImageUrl : undefined)
                      : undefined
                  const previewFormat = isTileSizeOption
                    ? option.value
                    : item?.format
                  const materialColor = isTileTypeOption
                    ? option.value
                    : item?.frame_color
                  const isSmallFormat = showImage && option.value === 27
                  const coverPreviewImage =
                    isCoverDropdown ? getCoverPreviewImage(option.value) : undefined
                  const coverPreviewSizeClass = isSmallFormat
                    ? "max-w-[86px] max-h-[86px] lg:max-w-[92px] lg:max-h-[92px]"
                    : "max-w-[100px] max-h-[100px] lg:max-w-[104px] lg:max-h-[104px]"

                  return (
                    <button
                      key={String(option.value)}
                      type="button"
                      onClick={() => handleOptionClick(dropDown, option)}
                      disabled={isDisabled}
                      aria-pressed={isSelected}
                      data-testid={optionTestId}
                      className={`flex flex-col items-center gap-2 transition-colors py-1 px-0.5 bg-transparent border-0 ${
                        isDisabled ? "opacity-50 cursor-not-allowed" : "opacity-100 cursor-pointer"
                      }`}
                    >
                      <div
                        className="flex items-center justify-center overflow-hidden w-[100px] h-[100px] lg:w-[112px] lg:h-[112px]"
                        data-testid={`${optionTestId}-preview`}
                        style={{ borderRadius: 0 }}
                      >
                        {isTileTypeOption || isTileSizeOption ? (
                          renderTilePreview(
                            previewFormat,
                            frameOverlayForTile,
                            materialColor
                          )
                        ) : showImage && isBookProduct && coverPreviewData ? (
                          <div
                            className="relative flex items-center justify-center w-full h-full overflow-hidden"
                            style={{ borderRadius: 0 }}
                          >
                            <CoverPreview
                              data={coverPreviewData}
                              className={`max-w-full max-h-full ${coverPreviewSizeClass} ${
                                isWideCoverPreview ? "w-auto h-full" : "h-full w-auto"
                              }`}
                            />
                          </div>
                        ) : coverPreviewImage ? (
                          <img
                            src={coverPreviewImage}
                            alt=""
                            className="object-cover w-full h-full"
                            style={{ borderRadius: 0 }}
                          />
                        ) : showImage && imageUrl ? (
                          <div
                            className="relative flex items-center justify-center w-full h-full overflow-hidden"
                            style={{ borderRadius: 0 }}
                          >
                            <img
                              src={imageUrl}
                              alt="Product preview"
                              className={`object-contain ${
                                isSmallFormat
                                  ? "w-[86px] h-[86px] lg:w-[92px] lg:h-[92px]"
                                  : "w-[100px] h-[100px] lg:w-[104px] lg:h-[104px]"
                              }`}
                              style={{ borderRadius: 0 }}
                            />
                          </div>
                        ) : (
                          <div
                            className="w-full h-full"
                            style={{
                              backgroundColor: "rgba(247, 247, 247, 1)",
                              borderRadius: 0,
                            }}
                          />
                        )}
                      </div>

                      <span
                        className={`relative inline-flex items-center justify-center text-sm text-center px-3 py-1 rounded transition-colors text-secondary-foreground ${
                          isSelected ? "font-ploni-demibold pr-7" : "font-ploni"
                        }`}
                        data-testid={`${optionTestId}-label`}
                        dir="auto"
                        style={{
                          unicodeBidi: "isolate",
                          backgroundColor: isSelected ? "rgba(234, 244, 216, 1)" : "transparent",
                        }}
                      >
                        <span className="w-full text-center">{option.label}</span>
                        {isSelected && (
                          <SelectedIcon
                            className="absolute right-2 w-4 h-4 text-secondary-foreground [&>path]:fill-current"
                            aria-hidden="true"
                          />
                        )}
                      </span>
                    </button>
                  )
                })}
            </div>

            {dropDown.isError && dropDown.helperText && (
              <p className="text-sm text-red-500 text-center">
                {dropDown.helperText}
              </p>
            )}
          </div>

          {index < dropDowns.length - 1 && (
            <div className="mx-4 h-px bg-disable" aria-hidden="true" />
          )}
        </div>
      ))}
    </div>
  )
}
